import { Reorder } from "motion/react";
import { useMemo } from "react";
import { Checkbox, cn, V2Label } from "web-design-system";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { ReorderableColumn } from "./reorderable-column";

export interface FieldConfig {
  /** correspond to id of the column in table */
  id: string;
  /** controls the visibility of the field */
  visible: boolean;
  /** Groups the field - defaults to general */
  group?: string;
  /** Whether a filed can be reordered, made invisible */
  configurable?: boolean;
  /** Display label in edit field popover / dialog */
  label?: string;
}

export interface EditFieldsProps {
  columnsConfig: FieldConfig[];
  className?: string;
  onReorder?: (values: string[]) => void;
  onVisibilityChange?: (values: VisibilityState) => void;
}

export type EditableFieldDef<T> = ColumnDef<T> & {
  group?: string;
  configurable?: boolean;
  label?: string;
};

export function EditColumns(props: EditFieldsProps) {
  const { columnsConfig, onReorder, onVisibilityChange } = props;

  const groupedColumns = useMemo(() => {
    return columnsConfig.reduce(
      (acc, column) => {
        const group = column.group || "General";
        acc[group] = [...(acc[group] || []), column];
        return acc;
      },
      {} as Record<string, EditFieldsProps["columnsConfig"]>
    );
  }, [columnsConfig]);

  return (
    <div className="flex flex-col gap-4">
      <div id="visible-metrics" className="flex flex-col gap-4">
        <h6>Visible fields</h6>
        <Reorder.Group
          onReorder={(values) => onReorder?.(values)}
          values={columnsConfig.map((c) => c.id)}
        >
          {columnsConfig.map((c) => (
            <ReorderableColumn
              key={c.id}
              columnConfig={c}
              onVisibilityChange={(checked) =>
                onVisibilityChange?.({ [c.id]: checked })
              }
            />
          ))}
        </Reorder.Group>
      </div>
      {Object.entries(groupedColumns).map(([group, columns]) => (
        <div id={group.toLocaleLowerCase()} key={group} className="mb-2">
          <h6 className="mb-2">{group}</h6>
          {columns.map((column) => (
            <div
              key={column.id}
              className="flex flex-row p-2 gap-2 items-center"
            >
              <Checkbox
                id={column.id}
                checked={column.visible}
                disabled={!column.configurable}
                onCheckedChange={(checked) =>
                  onVisibilityChange?.({ [column.id]: !!checked })
                }
              />
              <V2Label htmlFor={column.id}>{column.label}</V2Label>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function TableOfContents(props: {
  columnsConfig: EditFieldsProps["columnsConfig"];
  activeSection: string;
  header: string;
}) {
  const { columnsConfig, activeSection } = props;

  const groupedColumns = useMemo(() => {
    return columnsConfig.reduce(
      (acc, column) => {
        const group = column.group || "General";
        acc[group] = [...(acc[group] || []), column];
        return acc;
      },
      {} as Record<string, EditFieldsProps["columnsConfig"]>
    );
  }, [columnsConfig]);

  return (
    <div
      className={cn(
        "flex flex-3 gap-8 p-6 bg-gradient-to-b from-secondary to-muted flex-col"
      )}
    >
      <span className="text-lg font-semibold">{props.header}</span>
      <div className="flex flex-col gap-2">
        <button
          className="px-3 h-11 rounded-xl text-sm data-[status=active]:bg-background data-[status=active]:font-medium flex items-center font-normal text-left cursor-pointer hover:bg-background/50 transition-colors"
          onClick={() => {
            document.getElementById("visible-metrics")?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }}
          data-status={
            activeSection === "visible-metrics" ? "active" : undefined
          }
        >
          Visible fields
        </button>
        {Object.keys(groupedColumns).map((key) => (
          <button
            className="px-3 h-11 rounded-xl text-sm font-normal data-[status=active]:bg-background data-[status=active]:font-medium flex items-center text-left cursor-pointer hover:bg-background/50 transition-colors"
            key={key}
            onClick={() => {
              document.getElementById(key.toLocaleLowerCase())?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }}
            data-status={
              activeSection === key.toLocaleLowerCase() ? "active" : undefined
            }
          >
            {key}
          </button>
        ))}
      </div>
    </div>
  );
}
